import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { t } from '@hefe/i18n';
import { RequestBasePostInventoryCategory } from '@hefe/models';
import { InventoryApi, InventorySlice } from '@hefe/store';
import {
  Button,
  Divider,
  Input,
  Popconfirm,
  Skeleton,
  Space,
  Tree,
} from 'antd';
import { DataNode } from 'antd/es/tree';
import { Key, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../store/store';
import { inventoryNavigationTreeData } from '../../services/inventory.service';

export default function InventoryNavigationComponent() {
  const expandedKeys = useSelector(InventorySlice.expandedKeysSelector);
  const selectedKey = useSelector(InventorySlice.selectedKeySelector);
  const dispatch = useAppDispatch();

  const { data: navigation = [], isFetching: isFetchingNavigation } =
    InventoryApi.useFetchNavigationQuery();

  const [saveCategory, { isLoading: isSavingCategory }] =
    InventoryApi.useAddCategoryMutation();

  const [deleteCategory, { isLoading: isDeletingCategory }] =
    InventoryApi.useDeleteCategoryMutation();

  const [updateNavigation, { isLoading: isUpdatingNavigation }] =
    InventoryApi.useUpdateCategoryNavigationMutation();

  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    selectedKey ? parseInt(selectedKey) : undefined
  );

  const onSaveCategoryConfirm = async (edit: boolean) => {
    let data: RequestBasePostInventoryCategory = {
      name: newCategoryName,
    };

    if (edit) {
      data = {
        ...data,
        id: selectedCategory,
      };
    }

    await saveCategory(data);
    setNewCategoryName('');
  };

  const onSelectTreeKeys = (keys: Key[]) => {
    if (keys && keys.length === 1) {
      const key = keys[0] as string;
      dispatch(InventorySlice.actions.setSelectedKey(key));
      if (key === 'uncategorized') {
        setSelectedCategory(0);
      } else {
        setSelectedCategory(parseInt(key));
      }
    }
  };

  const onDropCategory = (
    categoryId: number,
    destinationId: number | undefined,
    asChild: boolean,
    position: number
  ) => {
    if (isNaN(categoryId)) {
      return;
    }

    updateNavigation({
      category_id: categoryId,
      destination_category_id: destinationId,
      as_child: asChild,
      position: position,
    })
      .unwrap()
      .catch(() => undefined);
  };

  const onDeleteCategory = () => {
    if (selectedCategory === undefined || isNaN(selectedCategory)) {
      return;
    }

    deleteCategory(selectedCategory)
      .unwrap()
      .then(() => {
        // Go to uncategorized
        setSelectedCategory(0);
      })
      .catch((_) => undefined);
  };

  const treeData: DataNode[] = useMemo(() => {
    return inventoryNavigationTreeData(navigation);
  }, [navigation]);

  return (
    <>
      <Skeleton
        active
        loading={isFetchingNavigation || isUpdatingNavigation}
        paragraph={{ rows: 8 }}
        title={false}
        style={{ paddingRight: 20 }}
      >
        <Tree
          treeData={treeData}
          onSelect={(keys) => onSelectTreeKeys(keys)}
          autoExpandParent={true}
          expandedKeys={expandedKeys}
          onExpand={(keys) => {
            dispatch(InventorySlice.actions.setExpandedKeys(keys));
          }}
          draggable
          selectedKeys={
            selectedKey
              ? [selectedKey]
              : selectedCategory === 0
              ? ['uncategorized']
              : []
          }
          onDrop={(info) => {
            onDropCategory(
              parseInt(info.dragNode.key + ''),
              parseInt(info.node.key + ''),
              !info.dropToGap,
              info.dropPosition
            );
          }}
        />
      </Skeleton>
      <Space direction="vertical" size={0}>
        <Divider />
        {selectedCategory !== undefined &&
          !isNaN(selectedCategory) &&
          selectedCategory > 0 && (
            <Popconfirm
              title={t('inventory.categories.delete.title')}
              description={t('inventory.categories.delete.description')}
              onConfirm={onDeleteCategory}
              overlayStyle={{ maxWidth: 300 }}
              icon={null}
            >
              <Button
                loading={isDeletingCategory}
                type="text"
                icon={<DeleteOutlined />}
              >
                {t('inventory.categories.delete.title')}
              </Button>
            </Popconfirm>
          )}

        {!!selectedCategory &&
          !isNaN(selectedCategory) &&
          selectedCategory > 0 && (
            <Popconfirm
              title={
                <div>
                  <Input
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    value={newCategoryName}
                    placeholder={t('inventory.categories.singular')}
                  />
                </div>
              }
              onConfirm={() => onSaveCategoryConfirm(true)}
              okButtonProps={{ disabled: newCategoryName.length < 3 }}
              icon={null}
            >
              <Button
                loading={isSavingCategory}
                type="text"
                icon={<EditOutlined />}
              >
                {t('inventory.categories.rename')}
              </Button>
            </Popconfirm>
          )}

        <Popconfirm
          title={
            <div>
              <Input
                onChange={(e) => setNewCategoryName(e.target.value)}
                value={newCategoryName}
                placeholder={t('inventory.categories.singular')}
              />
            </div>
          }
          onConfirm={() => onSaveCategoryConfirm(false)}
          okButtonProps={{ disabled: newCategoryName.length < 3 }}
          icon={null}
        >
          <Button
            loading={isSavingCategory}
            type="text"
            icon={<PlusCircleOutlined />}
          >
            {t('inventory.categories.add')}
          </Button>
        </Popconfirm>
      </Space>
    </>
  );
}
